import {
  Cryoprotectantv4,
  CrystalCryoDTO,
  CrystalSoakingDTO,
  ResponseType,
  Soakingv4,
} from 'business/models/Models';
import CrystalService from 'business/services/CrystalService';
import SynchrotronTripService from 'business/services/SynchrotronTripService';
import { AccordionCardProps } from 'components/card/AccordionCards';
import { RMSvgIconProps } from 'components/customComponents/CommonComponents/RMSvgIcon';
import { useExperimentStates } from 'components/Experiment/hooks';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import { cloneDeep } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useRMDispatch, useRMSelector } from 'redux/hooks';
import { useSelectedTripsCrystals, useSynchTrip } from 'redux/hooks/synchrotron';
import { setIsLockDrawing } from 'redux/reducers/experimentDataReducer';
import {
  setCheckedCrystalIDs,
  setSelectedDrawing,
} from 'redux/reducers/experimentReducer';
import {
  setHarvestingContentView,
  setCrystalTrips,
  setSelectedTripId,
  setSelectedTripsCrystals,
  setSelectedTripsCrystalIds,
} from 'redux/reducers/synchrotronTripReducer';
import { selectCheckedCrystals } from 'redux/selectors/StateSelectors';
import { openNode, showAlertSnackBar } from 'utils/Global';
import { EHarvestingView } from '../constants';
import { useCryoAgents, useSoakingAgents } from '../selectAgents/hooks';
import { IndexDBBuffer } from 'components/plateviewer/experiment/IndexDBBuffer';
import { useCrystals, useCurrentTripCheckedCrystals } from 'redux/hooks/crystal';
import CryoprotectantService from 'business/services/CryoprotectantService';
import SoakingService from 'business/services/SoakingService';
import { EDrawingType } from 'components/plateviewer/well/Well';
import {
  useSelectedDropImageParameters,
  useLastSelectedWellNavigationDetails,
  useSelectedDrawingOnCanvas,
} from 'redux/hooks/experiment';
import {
  removeDrawingsByIds,
  setDrawingTool,
  unSelectDrawing,
  updateDrawing,
  updateMultipleDrawings,
} from 'redux/reducers/wellReducer';
import {
  CrystalDrawing,
  DrawingType,
  OverlayTools,
} from 'components/well/drawingManager/constants';
import { useSelectedDrawingTool, useSelectedWellDrawing } from 'redux/hooks/well';
import {
  DropImageParameters,
  WellNavigationDetails,
} from 'components/Experiment/constants';

export const useCurrentDropRegion = () => {
  const { exp } = useExperimentStates();
  const wellNavigationDetails = useLastSelectedWellNavigationDetails();
  const selectedDropImageParameters = useSelectedDropImageParameters();
  const { wellNum, plateID } = wellNavigationDetails || {};
  const { dropNumber, regionName } = selectedDropImageParameters || {};
  const selectedRegion = exp?.regions?.find((r) => r.name === regionName);
  if (selectedRegion?.drops) {
    const regionDetail = selectedRegion.drops.find(
      (d) =>
        d.plateId == plateID && d.wellNumber == wellNum && d.dropNumber == dropNumber,
    );
    if (regionDetail) {
      return regionDetail.regionId;
    }
  }
  return undefined;
};

export const useOnCrystalCheckHandler = () => {
  const dispatch = useRMDispatch();
  const wellNavigationDetails = useLastSelectedWellNavigationDetails();
  const selectedDropImageParameters = useSelectedDropImageParameters();
  const { wellNum, plateID } = wellNavigationDetails || {};
  const { dropNumber, regionName } = selectedDropImageParameters || {};

  const handleOnCrystalCheckToggle = (crystal: CrystalDrawing) => {
    dispatch(
      updateDrawing({
        plateId: plateID,
        wellNumber: wellNum,
        dropNumber,
        regionName,
        id: crystal.id,
        drawingType: DrawingType.CRYSTAL,
        drawingUpdateDetails: { isChecked: !crystal.isChecked },
      }),
    );
    if (crystal.isChecked) {
      dispatch(unSelectDrawing());
    }
  };

  return handleOnCrystalCheckToggle;
};
export const useOnHarvestNewCrystal = () => {
  const dispatch = useRMDispatch();
  const drawingTool = useSelectedDrawingTool();
  return () => {
    dispatch(
      setDrawingTool(
        drawingTool === OverlayTools.CRYSTAL
          ? OverlayTools.POINTER
          : OverlayTools.CRYSTAL,
      ),
    );
  };
};

export const useOnDeleteCrystals = () => {
  const checkedCrystals = useCurrentTripCheckedCrystals();
  const dispatch = useRMDispatch();
  const crystalService = new CrystalService();
  return async (
    plateId: number,
    wellNumber: number,
    dropNumber: number,
    regionName: string,
  ) => {
    const response = await crystalService.DeleteCrystals(checkedCrystals);
    if (response.responseType === ResponseType.Error) {
      showAlertSnackBar(EAlertType.ERROR, ESnackBarPosition.TOP, response.message);
      return;
    }
    dispatch(
      removeDrawingsByIds({
        ids: checkedCrystals,
        drawingType: DrawingType.CRYSTAL,
        plateId,
        wellNumber,
        dropNumber,
        regionName,
      }),
    );
    dispatch(setCheckedCrystalIDs([]));
    showAlertSnackBar(EAlertType.SUCCESS, ESnackBarPosition.TOP, 'Crystals deleted');
  };
};

export const useOnCopyCrystals = () => {
  const crystals = useCrystals();
  const checkedCrystals = useCurrentTripCheckedCrystals();
  if (checkedCrystals?.length === 1) {
    const copiedCryoIds = crystals
      .find((x) => x.id === checkedCrystals[0])
      ?.cryoprotectants?.map((cryo) => {
        return cryo.id;
      });

    const copiedSoakingIds = crystals
      .find((x) => x.id === checkedCrystals[0])
      ?.soakings?.map((soaking) => {
        return soaking.id;
      });
    return async () => {
      const BUFFER = new IndexDBBuffer();
      await BUFFER.writeJson(null, null, null, [
        {
          value: 'crystalData',
          cryoprotectantIds: copiedCryoIds,
          soakingIds: copiedSoakingIds,
        },
      ]);
      showAlertSnackBar(
        EAlertType.SUCCESS,
        ESnackBarPosition.TOP,
        'Crystals Copied Successfully',
      );
    };
  }
};

export const useOnPasteCryo = () => {
  const dispatch = useRMDispatch();
  const BUFFER = new IndexDBBuffer();
  const crystals: CrystalDrawing[] = cloneDeep(useCrystals());
  const crystalIds = [...useCurrentTripCheckedCrystals()];
  const cryoAgents = [...useCryoAgents()];
  const wellNavigationDetails = useLastSelectedWellNavigationDetails();
  const selectedDropImageParameters = useSelectedDropImageParameters();
  const { wellNum, plateID } = wellNavigationDetails || {};
  const { dropNumber, regionName } = selectedDropImageParameters || {};

  return async () => {
    const copiedCrystalInfo = await BUFFER.ReadJSONCopyCrystals();
    const cryoCopiedIds = copiedCrystalInfo[0]?.cryoprotectantIds;
    if (!cryoCopiedIds) {
      showAlertSnackBar(
        EAlertType.ERROR,
        ESnackBarPosition.TOP,
        'Clipboard is empty for cryoprotectants',
      );
      return;
    }
    const getCrystalCryoDTO = cryoCopiedIds?.flatMap((agentId) =>
      crystalIds?.map(
        (crystalId) =>
          ({ cryoprotectantID: agentId, crystalId: crystalId } as CrystalCryoDTO),
      ),
    );
    const updatedCrystals = crystals?.map((crystal) => {
      if (crystalIds?.includes(crystal.id)) {
        crystal.cryoprotectants = cryoCopiedIds
          ?.map((agent) => {
            const existingCyro = cryoAgents.find((x) => x.id === agent);
            if (!existingCyro) {
              return null;
            }
            return {
              id: agent,
              ingredient: existingCyro.label,
            } as Cryoprotectantv4;
          })
          .filter((cyro) => cyro);
      }
      return { id: crystal.id, cryoprotectants: crystal.cryoprotectants };
    });
    await CryoprotectantService.getInstance().assignCryosToCrystals(getCrystalCryoDTO);
    dispatch(
      updateMultipleDrawings({
        plateId: plateID,
        wellNumber: wellNum,
        dropNumber: dropNumber,
        regionName: regionName,
        drawingType: DrawingType.CRYSTAL,
        drawingUpdateDetails: updatedCrystals,
      }),
    );
    showAlertSnackBar(
      EAlertType.SUCCESS,
      ESnackBarPosition.TOP,
      'Crystal Cryoprotectants Pasted Successfully',
    );
  };
};

export const useOnPasteSoaking = () => {
  const dispatch = useRMDispatch();
  const BUFFER = new IndexDBBuffer();
  const crystals = cloneDeep(useCrystals()) as CrystalDrawing[];
  const crystalIds = useCurrentTripCheckedCrystals();
  const soakingAgents = useSoakingAgents();
  const wellNavigationDetails = useLastSelectedWellNavigationDetails();
  const selectedDropImageParameters = useSelectedDropImageParameters();
  const { wellNum, plateID } = wellNavigationDetails || {};
  const { dropNumber, regionName } = selectedDropImageParameters || {};

  return async () => {
    const copiedCrystalInfo = await BUFFER.ReadJSONCopyCrystals();
    const soakingCopiedIds = copiedCrystalInfo[0]?.soakingIds;
    if (!soakingCopiedIds) {
      showAlertSnackBar(
        EAlertType.ERROR,
        ESnackBarPosition.TOP,
        'Clipboard is empty for soaking',
      );
      return;
    }
    const getCrystalSoakingDTO = soakingCopiedIds?.flatMap((agentId) =>
      crystalIds?.map(
        (crystalId) =>
          ({ soakingId: agentId, crystalId: crystalId } as CrystalSoakingDTO),
      ),
    );
    const updatedCrystals = crystals?.map((crystal) => {
      if (crystalIds.includes(crystal.id)) {
        crystal.soakings = soakingCopiedIds
          ?.map((agent) => {
            const existingSoaking = soakingAgents?.find((x) => x.id === agent);
            if (!existingSoaking) {
              return null;
            }

            return {
              id: agent,
              ingredient: existingSoaking.label,
            } as Soakingv4;
          })
          .filter((soaking) => soaking);
      }
      return { id: crystal.id, soakings: crystal.soakings };
    });
    await SoakingService.getInstance().assignSoakingToCrystals(getCrystalSoakingDTO);
    dispatch(
      updateMultipleDrawings({
        plateId: plateID,
        wellNumber: wellNum,
        dropNumber: dropNumber,
        regionName: regionName,
        drawingType: DrawingType.CRYSTAL,
        drawingUpdateDetails: updatedCrystals,
      }),
    );
    showAlertSnackBar(
      EAlertType.SUCCESS,
      ESnackBarPosition.TOP,
      'Crystal Soakings Pasted Successfully',
    );
  };
};

export const useOnSelectAllCrystals = (
  allCrystalsSelected: boolean,
  setAllCrystalsSelect,
) => {
  const wellNavigationDetails = useLastSelectedWellNavigationDetails();
  const selectedDropImageParameters = useSelectedDropImageParameters();
  const selectedDrawing = useSelectedWellDrawing();
  const { wellNum, plateID } = wellNavigationDetails || {};
  const { dropNumber, regionName } = selectedDropImageParameters || {};
  const crystals = useSelectedTripsCrystals();
  const dispatch = useRMDispatch();
  return () => {
    if (!allCrystalsSelected) {
      const crystalsUpdateDetails = crystals.map((_crystal) => ({
        id: _crystal.id,
        isChecked: true,
      }));
      dispatch(
        updateMultipleDrawings({
          plateId: plateID,
          wellNumber: wellNum,
          dropNumber,
          regionName,
          drawingType: DrawingType.CRYSTAL,
          drawingUpdateDetails: crystalsUpdateDetails,
        }),
      );
      dispatch(setCheckedCrystalIDs(crystals.map((x) => x.id)));
    } else {
      const crystalsUpdateDetails = crystals.map((_crystal) => ({
        id: _crystal.id,
        isChecked: false,
      }));
      dispatch(
        updateMultipleDrawings({
          plateId: plateID,
          wellNumber: wellNum,
          dropNumber,
          regionName,
          drawingType: DrawingType.CRYSTAL,
          drawingUpdateDetails: crystalsUpdateDetails,
        }),
      );
      dispatch(setCheckedCrystalIDs([]));
      if (crystalsUpdateDetails?.some((x) => x.id === selectedDrawing?.drawingId)) {
        dispatch(unSelectDrawing());
      }
    }
    setAllCrystalsSelect(!allCrystalsSelected);
  };
};

export const useAllSynchTrips = async () => {
  const synchService = new SynchrotronTripService();
  return await synchService.getOpenSynchTrips();
};

export const useCrystalStates = () => {
  const { crystalsTrips, harvestingContentView, selectedTripId, allSynchTrips } =
    useSynchTrip();
  const checkedCrystals = useRMSelector(selectCheckedCrystals);
  const crystals = useCrystals();
  const selectedTripCrystals = useSelectedTripsCrystals();
  return {
    crystals,
    selectedTripCrystals,
    crystalsTrips,
    harvestingContentView,
    checkedCrystals,
    allSynchTrips,
    selectedTripId,
  };
};

export const useAssignCrystal = () => {
  const dispatch = useRMDispatch();
  return () => {
    dispatch(setHarvestingContentView(EHarvestingView.SELECTTRIP));
  };
};

export const useUnAssignCrystal = (crystalId: number) => {
  const { crystalsTrips } = useCrystalStates();
  const dispatch = useRMDispatch();
  const crystalService = new CrystalService();

  const removeFromAssigned = () => {
    let updatedCrystalTrips = [...crystalsTrips];
    updatedCrystalTrips = updatedCrystalTrips.filter((x) => x.crystalId !== crystalId);
    dispatch(setCrystalTrips(updatedCrystalTrips));
    showAlertSnackBar(EAlertType.SUCCESS, ESnackBarPosition.TOP, 'Crystal Unassigned');
  };

  return async () => {
    const response = await crystalService.unAssignCrystal(crystalId);
    if (response.responseType === ResponseType.Error) {
      showAlertSnackBar(EAlertType.ERROR, ESnackBarPosition.TOP, response.message);
      return;
    }
    removeFromAssigned();
  };
};

export const useFetchCrystalTrip = (autoFetch: boolean = true) => {
  const regionId = useCurrentDropRegion();
  const dispatch = useRMDispatch();
  const fetchCrystalTrips = useCallback(async () => {
    const crystalService = new CrystalService();
    const crystalTrips = await crystalService.getCrystalTripsByRegionId(regionId);
    dispatch(setCrystalTrips(crystalTrips));
  }, [regionId, dispatch]);
  useEffect(() => {
    if (!autoFetch) {
      return;
    }
    fetchCrystalTrips();
  }, [fetchCrystalTrips, autoFetch]);
  return { fetchCrystalTrips };
};

export const useTripCards = () => {
  const dispatch = useRMDispatch();
  const { crystals, crystalsTrips, selectedTripId } = useCrystalStates();
  const uniqueTrips = [
    ...new Set(crystalsTrips?.map((obj) => obj.synchrotronTripId).sort()),
  ];
  const icon: RMSvgIconProps = {
    name: 'SynchrotronTrip',
    fill: 'none',
    width: 20,
    height: 20,
  };
  const selectedDrawing = useSelectedDrawingOnCanvas();
  const [openedWindows, setOpenedWindows] = useState([]);

  const onClickTrip = (tripId: number) => {
    dispatch(setSelectedTripId(tripId));
    filterCrystals(tripId);
  };

  const onDoubleClickTrip = (tripId: number) => {
    const route = `/synchrotrontrip/${tripId}`;
    openNode(route, route, tripId, openedWindows, setOpenedWindows);
  };

  const filterCrystals = useCallback(
    (tripID: number) => {
      const crystalIds = !tripID
        ? crystals
            ?.filter((x) => !crystalsTrips?.some((val) => val.crystalId === x.id))
            ?.map((val) => val.id)
        : crystalsTrips
            ?.filter((x) => x.synchrotronTripId === tripID)
            ?.map((val) => val.crystalId);
      dispatch(
        setSelectedTripsCrystals(crystals?.filter((x) => crystalIds?.includes(x.id))),
      );
      dispatch(
        setSelectedTripsCrystalIds(
          crystals?.filter((x) => crystalIds?.includes(x.id))?.map((x) => x.id),
        ),
      );
      updateDrawingSelection();
    },
    [crystalsTrips, crystals],
  );

  const updateDrawingSelection = () => {
    if (selectedDrawing && selectedDrawing.type === EDrawingType.CRYSTAL) {
      if (!crystals.find((x) => x.id === selectedDrawing.id)) {
        dispatch(setSelectedDrawing(null));
      }
    }
  };

  const deleteTrip = async (tripId: number) => {
    const synchService = new SynchrotronTripService();
    const response = await synchService.deleteSynchTripFromExperiment(tripId);
    if (response.responseType === ResponseType.Error) {
      showAlertSnackBar(EAlertType.ERROR, ESnackBarPosition.TOP, response.message);
      return;
    }
    let updatedCrystalTrips = [...crystalsTrips];
    updatedCrystalTrips = updatedCrystalTrips.filter(
      (x) => x.synchrotronTripId !== tripId,
    );
    dispatch(setCrystalTrips(updatedCrystalTrips));
    dispatch(setSelectedTripId(0)); // Select default unassigned crystal card as selected card is removed
    showAlertSnackBar(
      EAlertType.SUCCESS,
      ESnackBarPosition.TOP,
      'Trip removed from experiment',
    );
  };

  const defaultCard = {
    heading: 'Unassigned Crystals',
    active: selectedTripId === 0,
    activeClass: 'active-card',
    customStyle: { textAlign: 'center' },
    icon: null,
    onClick: () => onClickTrip(0),
  } as AccordionCardProps;

  const cards = useMemo(() => {
    return uniqueTrips.map(
      (tripId: number) =>
        ({
          heading:
            [...crystalsTrips].filter((x) => x.synchrotronTripId === tripId)[0]
              .synchrotronTripName.length > 9
              ? [...crystalsTrips]
                  .filter((x) => x.synchrotronTripId === tripId)[0]
                  .synchrotronTripName.substring(0, 8) + '...'
              : [...crystalsTrips].filter((x) => x.synchrotronTripId === tripId)[0]
                  .synchrotronTripName,
          headingTooltip: crystalsTrips.filter((x) => x.synchrotronTripId === tripId)[0]
            .synchrotronTripName,
          subHeading: crystalsTrips
            .filter((x) => x.synchrotronTripId === tripId)[0]
            .dataCollectionDate?.toLocaleDateString('en-US'),
          active: selectedTripId === tripId,
          icon: icon,
          activeClass: 'active-card',
          onClick: () => onClickTrip(tripId),
          onDblClick: () => onDoubleClickTrip(tripId),
          onDelete: () => deleteTrip(tripId),
        } as AccordionCardProps),
    );
  }, [uniqueTrips, crystalsTrips, selectedTripId]);

  useEffect(() => {
    filterCrystals(selectedTripId);
  }, [filterCrystals, selectedTripId]);

  return [defaultCard, ...cards] as AccordionCardProps[];
};

export const useOnLockCrystal = () => {
  const dispatch = useRMDispatch();
  return () => {
    dispatch(setIsLockDrawing(true));
  };
};

export const useSingleCrystalCardClick = (crystal: CrystalDrawing) => {
  const dispatch = useRMDispatch();
  const crystals = useCrystals();
  const wellNavigationDetails = useLastSelectedWellNavigationDetails();
  const selectedDropImageParameters = useSelectedDropImageParameters();
  const { wellNum, plateID } = wellNavigationDetails || {};
  const { dropNumber, regionName } = selectedDropImageParameters || {};

  return () => {
    const crystalsUpdateDetails = crystals.map((_crystal) => {
      if (_crystal.id === crystal.id) {
        return { id: crystal.id, isChecked: true };
      }
      return { id: _crystal.id, isChecked: false };
    });
    dispatch(
      updateMultipleDrawings({
        plateId: plateID,
        wellNumber: wellNum,
        dropNumber,
        regionName: regionName,
        drawingType: DrawingType.CRYSTAL,
        drawingUpdateDetails: crystalsUpdateDetails,
      }),
    );
  };
};

export const useClearSelectedCrystals = () => {
  const dispatch = useRMDispatch();
  const crystals = useCrystals();
  return (
    wellNavigationDetails: WellNavigationDetails,
    wellImagingParameterDetails: DropImageParameters,
  ) => {
    const crystalsUpdateDetails = crystals.map((_crystal) => ({
      id: _crystal.id,
      isChecked: false,
    }));
    dispatch(
      updateMultipleDrawings({
        plateId: wellNavigationDetails.plateID,
        wellNumber: wellNavigationDetails.wellNum,
        dropNumber: wellImagingParameterDetails.dropNumber,
        regionName: wellImagingParameterDetails.regionName,
        drawingType: DrawingType.CRYSTAL,
        drawingUpdateDetails: crystalsUpdateDetails,
      }),
    );
    dispatch(setCheckedCrystalIDs([]));
  };
};
